import { useMemo } from 'react';

import { useListVaults, VaultType, type ListVaultsParams, type Vault } from '@/modules/api/codegen';
import { queryErrorMeta } from '@/modules/core/components/QueryProvider';

import { VAULTS_MAX_LIMIT } from '../constants';

const NO_VAULTS: Vault[] = [];

// Reported by the shared query cache rather than a per-observer effect: the sidebar and the settings
// table read this list at the same time, and one failed request must raise exactly one toast.
export const LIST_VAULTS_OPTIONS = { query: { meta: queryErrorMeta('error.vaults.fetch') } };

export const useListVaultsQuery = (params: ListVaultsParams) => {
    const query = useListVaults(params, LIST_VAULTS_OPTIONS);

    return {
        query,
        vaults: query.data?.data?.edges ?? NO_VAULTS,
        total: query.data?.data?.pageInfo?.count ?? 0,
    };
};

// Navigation and the route firewall need every vault the caller can reach, not a page of them, so
// this asks for the largest page the endpoint allows.
const ALL_VAULTS_PARAMS: ListVaultsParams = { limit: VAULTS_MAX_LIMIT };

export const useVaults = () => {
    const { query, vaults, total } = useListVaultsQuery(ALL_VAULTS_PARAMS);

    const { personalVault, teamVaults } = useMemo(
        () => ({
            personalVault: vaults.find(vault => vault.type === VaultType.personal),
            teamVaults: vaults.filter(vault => vault.type === VaultType.team),
        }),
        [vaults],
    );

    // A vault missing from a truncated list is not proof it is unreachable, and the firewall redirects
    // on exactly that inference.
    const isComplete = vaults.length >= total;

    return { query, vaults, personalVault, teamVaults, isComplete };
};
